import React, { useState } from "react";
import { useDCContext } from "@src/context";
import { useIsMobileWidth } from "@src/utils";
import { BackButton, Loader } from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import SupportForm from "@src/components/forms/operator/SupportForm";
import FAQList from "./FAQList";

const DefinitionsAndFAQsSection = ({
  mode = "OPERATOR",
  customer,
  handleOpenOnboarding = null,
  handleSubmitSupportForm = null,
  isLoadingBvrExclusions = false,
  bvrExclusions = {},
}) => {
  const dc = useDCContext();
  const customerCode = customer.customerCode.toLowerCase();
  const {
    countSimilarDynamicReplacements,
    existsDynamicReplacements,
    getDynamicReplacement,
  } = dc;

  const [openQ, setOpenQ] = useState(null);
  const isMobile = useIsMobileWidth();
  const isManager = mode === "MANAGER";

  const getDefinitions = () => {
    const definitionsPath = isManager ? "definitions__manager" : "definitions";
    const definitionsCount = countSimilarDynamicReplacements(
      `${definitionsPath}__def`,
    );
    let res = [];
    if (definitionsCount > 0) {
      for (let i = 0; i <= definitionsCount; i++) {
        if (existsDynamicReplacements(`${definitionsPath}__def${i + 1}`)) {
          const title = getDynamicReplacement(
            `${definitionsPath}__title${i + 1}`,
          );
          const definitionOverrideExists = existsDynamicReplacements(
            `charities__${customerCode}__definitions_overrides${
              isManager ? "__manager" : ""
            }__def${i + 1}`,
          );
          const definition = definitionOverrideExists
            ? getDynamicReplacement(
                `charities__${customerCode}__definitions_overrides${
                  isManager ? "__manager" : ""
                }__def${i + 1}`,
                {},
                "",
                false,
              )
            : getDynamicReplacement(
                `${definitionsPath}__def${i + 1}`,
                {},
                "",
                false,
              );
          res.push(
            <div key={`def-div-${i + 1}`} className="col-span-1">
              <CardContainer
                key={`definition${i + 1}`}
                padding={6}
                flexibleHeight={true}
              >
                <div className="flex justify-center w-full mb-4">
                  <h3 className="text-primary-500 font-bold text-xl">
                    {title}
                  </h3>
                </div>
                {definition}
              </CardContainer>
            </div>,
          );
        }
      }
    }

    const colCount = definitionsCount ? definitionsCount : 1;
    return (
      <div className={`grid grid-cols-${!!isMobile ? 1 : colCount} gap-4 px-4`}>
        {res}
      </div>
    );
  };

  const getFAQs = (categoryString) => {
    if (isLoadingBvrExclusions) return <Loader size="2xl" />;

    const FAQs = [];

    for (let i = 0; i <= countSimilarDynamicReplacements(categoryString); i++) {
      if (existsDynamicReplacements(`${categoryString}${i + 1}`)) {
        FAQs.push(`${categoryString}${i + 1}`);
      }
    }
    return (
      <FAQList
        faqDCKeys={FAQs}
        eventName={customer.preferredEventName.toLowerCase()}
        getDynamicContentFunc={getDynamicReplacement}
        handleOpenOnboarding={handleOpenOnboarding}
        exclusions={bvrExclusions}
        openQ={openQ}
        setOpenQ={setOpenQ}
      />
    );
  };

  const getFAQCategories = () => {
    const faqPath = isManager ? "faq__manager__cat" : "faq__cat";
    let res = [];

    for (let i = 0; i < countSimilarDynamicReplacements(faqPath); i++) {
      const categoryTitle = getDynamicReplacement(`${faqPath}${i + 1}__title`);
      const hasCharityFAQ = countSimilarDynamicReplacements(
        `charities__${customerCode}__${faqPath}${i + 1}__charity_q`,
      );
      let questions = getFAQs(`${faqPath}${i + 1}__q`);
      let charityQuestions = hasCharityFAQ
        ? getFAQs(`charities__${customerCode}__${faqPath}${i + 1}__charity_q`)
        : "";
      const shouldNotPush =
        !!categoryTitle?.props?.dangerouslySetInnerHTML?.__html.includes(
          "DO NOT SHOW",
        ) || !!categoryTitle?.props?.children.includes("DO NOT SHOW");

      if (!shouldNotPush) {
        res.push(
          <div key={`faq-category${i + 1}`} className="mb-6">
            <h4 className="text-primary-500 text-center md:text-left mb-4">
              {categoryTitle}
            </h4>
            {questions}
            {charityQuestions}
          </div>,
        );
      }
    }
    return res;
  };

  return (
    <section className="p-4">
      <div className="relative flex flex-col items-center justify-center mb-8 h-14">
        <div className="absolute self-start ml-4">
          <BackButton trackingActionKey={`${mode}_FAQ_BACK_BUTTON`} />
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold text-center leading-snug capitalize m-0">
          Definitions
        </h1>
      </div>
      <div className="flex flex-col items-center mb-8">
        <div className="flex flex-col md:flex-row md:space-x-4 gap-4 justify-between w-full py-4">
          {getDefinitions()}
        </div>
      </div>
      <div className="mx-auto  w-full flex flex-col items-center px-4">
        <h1 className="text-2xl md:text-4xl font-extrabold text-center leading-snug capitalize mb-8">
          Frequently Asked Questions
        </h1>
        <div className="flex flex-col justify-between w-full">
          {getFAQCategories()}
        </div>
      </div>
      {typeof handleSubmitSupportForm === "function" && (
        <div className="px-4">
          <SupportForm onSubmit={handleSubmitSupportForm} />
        </div>
      )}
    </section>
  );
};
export default DefinitionsAndFAQsSection;
